import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useQuery } from "@tanstack/react-query";

import { ANALYTICS_EVENTS, type ProjectVersionSwitchedProperties } from "analytics";
import { USER_API } from "config";
import { screenplaySaveGate } from "features/script-editor/screenplaySaveGate";
import { useProjectAuth } from "hooks/project/useProjectAuth";
import { useAnalytics } from "hooks/useAnalytics";
import { useAxios } from "hooks/useAxios";
import { useActivateVersion, useProjectVersions } from "hooks/useProjects/useVersions";
import type { ProjectState } from "hooks/useProjects/useProjects.types";
import { useUserCatalog } from "hooks/useSession";
import { projectKeys } from "queries/project.keys";
import {
  getSelectedDocument,
  selectSelectedDocumentIdForProject,
  setSelectedDocumentId,
} from "store/reducers/project";
import type { AppState } from "store/reducers/Reducer";
import { Permissions } from "utils/permissions/permissionConstants";
import { handleToast } from "utils/toasts";
import { draftActivationRegistry } from "./draftActivationRegistry";
import {
  mergeSwitcherProjects,
  sortRecentProjects,
  toProjectDrafts,
  toSwitcherProject,
  type ProjectDraft,
  type ProjectListRowPayload,
  type SwitcherProject,
} from "./switcherProjects";

const SWITCHER_PAGE_SIZE = 100;

/**
 * Projects of the workspace for the switcher, most recently changed first.
 * The user catalog lists every project. Licenses come from the first page of
 * `GET /projects`, fetched only while the switcher is open, and are merged in.
 */
export function useSwitcherProjects(workspaceId: string, enabled: boolean) {
  const axios = useAxios({ allResponse: false });
  const { data: catalog } = useUserCatalog();

  const listQuery = useQuery<SwitcherProject[]>({
    queryKey: projectKeys.switcher(workspaceId),
    enabled: enabled && Boolean(workspaceId),
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const response = await axios.get(USER_API.projects, {
        params: { workspace_id: workspaceId, take: SWITCHER_PAGE_SIZE, page: 1 },
      });
      const body = response as unknown as { data?: unknown } | ProjectListRowPayload[];
      const rows = Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : [];
      return (rows as ProjectListRowPayload[])
        .filter((row) => !row.archived)
        .map(toSwitcherProject);
    },
  });

  const catalogProjects = useMemo<SwitcherProject[]>(() => {
    const workspace = catalog?.workspaces.find(
      (candidate) => String(candidate.workspace_id) === String(workspaceId)
    );
    return (workspace?.projects ?? [])
      .filter((project) => !project.archived)
      .map((project) => ({
        projectId: String(project.project_id),
        name: project.name,
        license: null,
        updatedAt: project.updated_at ?? project.created_at ?? null,
      }));
  }, [catalog?.workspaces, workspaceId]);

  const projects = useMemo(
    () => sortRecentProjects(mergeSwitcherProjects(catalogProjects, listQuery.data)),
    [catalogProjects, listQuery.data]
  );

  return { projects, isLoading: listQuery.isLoading && catalogProjects.length === 0 };
}

/** The draft the user is viewing: their pick in this session, else the project's active one. */
export function useCurrentDraftId(project: Pick<ProjectState, "projectId" | "activeDocumentId">) {
  const selectedDocumentId = useSelector((state: AppState) =>
    selectSelectedDocumentIdForProject(state.project, project.projectId)
  );
  return selectedDocumentId ?? project.activeDocumentId ?? null;
}

/** Drafts of the project for the switcher, newest first. */
export function useProjectDrafts(project: Pick<ProjectState, "projectId" | "versions">): {
  drafts: ProjectDraft[];
  isLoading: boolean;
} {
  const versionsQuery = useProjectVersions(project.projectId);
  const drafts = useMemo(() => {
    const fromQuery = versionsQuery.data?.map((version) => ({
      documentId: String(version.document_id),
      createdAt: version.created_at,
    }));
    const fromProject = project.versions?.map((version) => ({
      documentId: String(version.documentId),
      createdAt: version.createdAt,
    }));
    return toProjectDrafts(fromQuery ?? fromProject ?? []);
  }, [project.versions, versionsQuery.data]);

  return { drafts, isLoading: versionsQuery.isLoading && drafts.length === 0 };
}

/**
 * Switches the draft the same way the version switcher always did: select it,
 * record `project_version_switched`, then make it the project's active draft.
 * Only people who can update the project may switch. When the screenplay
 * editor is open, its waiting edits are saved first; the switch happens only
 * once they are saved.
 *
 * One switch at a time per project: while a switch is pending, that
 * project's switching is off (even if its layout remounts), so the server
 * cannot end on a different draft than the one shown. A failed activation
 * puts the previous selection back, but only while the project is still open
 * and its attempted draft is still the selection.
 */
export function useSwitchProjectDraft(
  project: Pick<ProjectState, "projectId" | "activeDocumentId">
) {
  const projectId = String(project.projectId);
  const dispatch = useDispatch();
  const store = useStore<AppState>();
  const { trackEvent } = useAnalytics();
  const { canProject, isLoading } = useProjectAuth(projectId);
  const { mutateAsync: activate } = useActivateVersion(projectId);
  const isSwitching = useSyncExternalStore(draftActivationRegistry.subscribe, () =>
    draftActivationRegistry.isPending(projectId)
  );
  useEffect(() => draftActivationRegistry.mount(projectId), [projectId]);

  // The draft the server last confirmed as active. The cached project can lag
  // behind a successful activation until it refetches, so it is not enough.
  const serverActiveIdRef = useRef<string | null>(
    project.activeDocumentId ? String(project.activeDocumentId) : null
  );
  useEffect(() => {
    serverActiveIdRef.current = project.activeDocumentId ? String(project.activeDocumentId) : null;
  }, [project.activeDocumentId]);

  const canSwitchDraft = !isLoading && canProject(Permissions.PROJECT_UPDATE) && !isSwitching;

  const switchDraft = useCallback(
    async (draft: ProjectDraft) => {
      if (!canSwitchDraft || !draftActivationRegistry.start(projectId, draft.documentId)) return;
      try {
        // The open editor's waiting edits are saved before anything switches. When that save
        // fails or hangs, the pick, the server's active draft and the editor all stay. A link
        // clicked while it saves takes over, and this switch is dropped.
        if (screenplaySaveGate.hasOpenEditor(projectId)) {
          const outcome = await screenplaySaveGate.saveBefore(projectId).outcome;
          if (outcome !== "proceed") return;
          // A save that lands after the user left the project switches nothing.
          if (!draftActivationRegistry.isMounted(projectId)) return;
        }
        const previousSelection = getSelectedDocument(store.getState().project);
        dispatch(setSelectedDocumentId({ projectId, documentId: draft.documentId }));
        trackEvent(ANALYTICS_EVENTS.PROJECT_VERSION_SWITCHED, {
          project_id: projectId,
          document_id: draft.documentId,
          version_name: draft.label,
          source: "sidebar_switcher",
        } satisfies ProjectVersionSwitchedProperties);
        if (serverActiveIdRef.current === draft.documentId) return;
        try {
          await activate(draft.documentId);
          serverActiveIdRef.current = draft.documentId;
        } catch {
          // The selection is app-wide. Put the old one back only if this project
          // is still open and nothing has replaced the draft we tried.
          const stillCurrent =
            draftActivationRegistry.isMounted(projectId) &&
            store.getState().project?.selected_document_id === draft.documentId;
          if (stillCurrent) dispatch(setSelectedDocumentId(previousSelection));
          handleToast("Couldn't switch drafts. Try again.", "error");
        }
      } finally {
        draftActivationRegistry.finish(projectId);
      }
    },
    [activate, canSwitchDraft, dispatch, projectId, store, trackEvent]
  );

  return { canSwitchDraft, switchDraft, isSwitching };
}
